import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase/supabase';

export default function UpdatePassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'StudioSign | Update Password';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(updateError.message);
      setLoading(false);
      return;
    }

    // Sign out cleanly after password update so they log in fresh
    await supabase.auth.signOut();
    setSuccess(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center relative overflow-hidden">
      <div className="absolute w-[600px] h-[600px] bg-white/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-10 flex flex-col relative z-10 mx-4">
        {success ? (
          <div className="flex flex-col items-center text-center py-6">
            <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">Password updated.</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-8">
              Your password has been changed successfully. Please sign in with your new credentials.
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full bg-white text-black font-semibold tracking-wide py-3.5 rounded-xl hover:bg-zinc-200 transition-colors duration-300 ease-out"
            >
              Go to Login
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-3xl tracking-tight text-center mb-2">
              <span className="font-medium text-zinc-400">Studio</span>
              <span className="font-extrabold text-white">Sign</span>
            </h1>
            <p className="text-sm font-medium text-zinc-400 text-center mb-8">
              Set a new password for your account.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input
                type="password"
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="new-password"
                className="w-full bg-black/50 border border-white/5 rounded-xl px-4 py-3 text-white focus:border-white/20 outline-none transition-colors duration-300 ease-out placeholder:text-zinc-600 disabled:opacity-50"
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                disabled={loading}
                autoComplete="new-password"
                className="w-full bg-black/50 border border-white/5 rounded-xl px-4 py-3 text-white focus:border-white/20 outline-none transition-colors duration-300 ease-out placeholder:text-zinc-600 disabled:opacity-50"
              />

              {error && (
                <div className="text-red-400 text-sm font-medium text-center">{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black font-semibold tracking-wide py-3.5 rounded-xl mt-4 hover:bg-zinc-200 transition-colors duration-300 ease-out disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}